import messages from "messages";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
// components
import AspectRatio from "components/aspect-ratio";
import CancelButton from "components/buttons/cancel-button";
import {
  OrchidProAppointmentJoinButton,
  OrchidProGroupSessionJoinButton,
} from "components/Dashboard/DoctorDashboard/components/orchid-pro-join-button";
import NextButton from "components/pagination-buttons/components/next-button";
import PreviousButton from "components/pagination-buttons/components/previous-button";
import RoundedImageWithFallback from "components/rounded-image-with-fallback";
import CancelModal from "../views/one-one-sessions/components/layout/components/list-view/components/cancel-modal";
// import ConfirmStatusTag from "components/Dashboard/DoctorDashboard/components/confirm-status-tag";
// contexts
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
// utils
import getItemsByTime, {
  Item,
} from "components/Dashboard/utility/getItemsByTime";
import { getOrchidProProfileImageUrl } from "utils/formatter/orchidPro";
import getClasses from "utils/getClasses";
// hooks
import useToggleModal from "hooks/useToggleModal";
// styles
import useFetchInvoicesForAppointments from "hooks/useFetchInvoicesForAppointments";
import { track } from "utils/amplitude";
import styles from "./SessionCardView.module.scss";

const ANALYTICS_COMPONENT_NAME = "upcoming-session-cards";

const SessionCardView = ({ futureSessions, analyticsPageName }) => {
  const { isPhonePortrait } = usePhonePortraitContext();

  const { appointmentsWithInvoices } =
    useFetchInvoicesForAppointments(futureSessions);

  const { show, open, close } = useToggleModal(false);
  const [session, setSession] = useState<Item | undefined>(undefined);
  const [countPerPage, setCountPerPage] = useState(3);

  const updateDimensions = () => {
    setCountPerPage(window.innerWidth >= 1920 ? 3 : 2);
  };

  const handleOpenModal = (arg: Item) => {
    setSession(arg);
    open();
  };

  const handleCloseModal = () => {
    setSession(undefined);
    close();
  };

  // page start
  const [page, setPage] = useState(0);

  const items = useMemo(() => {
    return getItemsByTime(appointmentsWithInvoices, [], +new Date());
  }, [appointmentsWithInvoices]);

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const cancelledItems = items.filter(
    (el) => el.typeId === "ONE_ON_ONE" && el.statusFlags.cancelledOrAbandoned,
  );

  const itemsInList = items.filter((el) => !cancelledItems.includes(el));

  const pageMax = Math.ceil(itemsInList.length / countPerPage);

  const itemsPerPage = itemsInList.slice(
    countPerPage * page,
    countPerPage * (page + 1),
  );

  return (
    <>
      {itemsInList.length === 0 ? (
        <div className={styles["sessions-section__empty"]}>
          No upcoming sessions.
        </div>
      ) : (
        <div className="d-flex justify-content-end">
          <PreviousButton
            disabled={page === 0}
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "prev-page-button"],
                "click",
              );
              setPage((page) => page - 1);
            }}
          />
          <NextButton
            disabled={page >= pageMax - 1}
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "next-page-button"],
                "click",
              );
              setPage((page) => page + 1);
            }}
          />
        </div>
      )}
      <Row>
        {itemsPerPage.map((el, i) => (
          <Col
            className={
              !isPhonePortrait ? styles["sessions-section__col"] : "p-0 mb-3"
            }
            style={{
              flex: !isPhonePortrait ? `0 0 ${1 / countPerPage}` : "auto",
            }}
            key={`${i}-${el.timeslotId}`}
          >
            <AspectRatio
              ratio={1.64}
              wrapperClassName={styles["session-card__wrapper"]}
              contentClassName={styles["session-card__content"]}
            >
              <Card
                className={getClasses(
                  `rounded-lg mb-1 w-100 mr-2 h-100`,
                  el.canJoinForPro && "background-bg-blue",
                )}
              >
                <Card.Body className="pb-0">
                  {/* {session.speakers && session.speakers.length > 1 ? (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="rg-14">{session.speakers.length} speakers</span>
                      <SpeakerIcons session={session} />
                    </div>
                  ) : ( */}

                  {/* {el.typeId === "ONE_ON_ONE" &&
                    el.statusFlags.unconfirmedInvite && (
                      <div
                        className={styles["sessions-section__unconfirm-tag"]}
                      >
                        <ConfirmStatusTag message={"Pending"} />
                      </div>
                    )} */}
                  <div className="d-flex justify-content-between align-items-start">
                    <RoundedImageWithFallback
                      src={getOrchidProProfileImageUrl(
                        el.orchidPro.cognito_sub,
                      )}
                      width={40}
                      height={40}
                    />
                    <span className="pl-3" style={{ flex: "1" }}>
                      <Card.Text className="rg-14 mb-0 deep-blue">
                        {/* time (length) */}
                        {`${new Date(el.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })} (${el.duration} ${messages.minutesShort})`}
                      </Card.Text>
                      <Card.Text className="md-16 mb-0 module line-clamp-1">
                        {/* name */}
                        {el.title}
                      </Card.Text>
                      <Card.Text className="rg-12 mb-0 gray01">
                        {/* attendees */}
                        {typeof el.attendees === "number"
                          ? `${el.attendees} attendee(s)`
                          : el.attendees}
                      </Card.Text>
                    </span>
                  </div>
                  {/* )} */}
                  <Card.Text className="rg-12 gray01 mt-3 module line-clamp-1">
                    {el.typeId === "GROUP_SESSION" && el.description}
                  </Card.Text>
                </Card.Body>
                <Card.Footer
                  className={styles["sessions-section__card-footer"]}
                >
                  {/* actions */}
                  {el.typeId === "ONE_ON_ONE" &&
                  el.statusFlags.unconfirmedInvite ? (
                    el.canCancelForPro && (
                      <CancelButton
                        className={getClasses(
                          "ml-1",
                          styles["sessions-section__button"],
                        )}
                        onClick={() => {
                          track(
                            analyticsPageName,
                            [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                            "click",
                          );
                          handleOpenModal(el);
                        }}
                      />
                    )
                  ) : (
                    <>
                      {el.canJoinForPro &&
                        (el.typeId === "ONE_ON_ONE" ? (
                          <OrchidProAppointmentJoinButton
                            session={el}
                            className={styles["sessions-section__button"]}
                            title={"Join session"}
                          />
                        ) : (
                          <OrchidProGroupSessionJoinButton
                            groupSession={el}
                            className={styles["sessions-section__button"]}
                            title={"Join session"}
                          />
                        ))}
                      {el.canCancelForPro &&
                        (el.typeId === "ONE_ON_ONE" &&
                        el.statusFlags.usingInvoicing ? null : (
                          <CancelButton
                            className={getClasses(
                              "ml-1",
                              styles["sessions-section__button"],
                            )}
                            onClick={() => {
                              track(
                                analyticsPageName,
                                [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                                "click",
                              );
                              handleOpenModal(el);
                            }}
                          />
                        ))}
                    </>
                  )}
                </Card.Footer>
              </Card>
            </AspectRatio>
          </Col>
        ))}
        {countPerPage - itemsPerPage.length !== countPerPage &&
          Array.from({ length: countPerPage - itemsPerPage.length }).map(
            (_, i) => {
              return (
                <Col
                  key={i}
                  className={styles["sessions-section__col"]}
                  style={{ flex: `0 0 ${1 / countPerPage}` }}
                ></Col>
              );
            },
          )}
      </Row>
      {/* modals */}
      <CancelModal show={show} onHide={handleCloseModal} session={session} />
    </>
  );
};
export default SessionCardView;
